import * as React from 'react';
import { createContext, ReactNode, useContext, useCallback } from 'react';
import { supabase } from '../providers/supabase/supabase';

const STORAGE_KEY = 'hpt-site-status';

export type SiteStatus = 'Live' | 'System Update' | 'Beta';

const DEFAULT_STATUS: SiteStatus = 'Live';

function readStoredStatus(): SiteStatus {
    if (typeof window === 'undefined') return DEFAULT_STATUS;
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (
            stored === 'Live' ||
            stored === 'System Update' ||
            stored === 'Beta'
        ) {
            return stored;
        }
    } catch {
        // ignore
    }
    return DEFAULT_STATUS;
}

interface SiteStatusContextValue {
    siteStatus: SiteStatus;
    setSiteStatus: (status: SiteStatus) => void;
}

const SiteStatusContext = createContext<SiteStatusContextValue>({
    siteStatus: DEFAULT_STATUS,
    setSiteStatus: () => {},
});

export function SiteStatusProvider({ children }: { children: ReactNode }) {
    const [siteStatus, setState] = React.useState<SiteStatus>(readStoredStatus);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data, error } = await Promise.resolve(
                    supabase
                        .from('site_status')
                        .select('value')
                        .eq('key', 'current')
                        .single()
                );
                if (!cancelled && !error && data?.value) {
                    const v = data.value as SiteStatus;
                    if (v === 'Live' || v === 'System Update' || v === 'Beta') {
                        setState(v);
                        try {
                            localStorage.setItem(STORAGE_KEY, v);
                        } catch {
                            // ignore
                        }
                    }
                }
            } catch {
                // ignore
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const setSiteStatus = useCallback((status: SiteStatus) => {
        setState(status);
        try {
            localStorage.setItem(STORAGE_KEY, status);
        } catch {
            // ignore
        }
        // Persistence with user/message/code is done from Admin Panel when saving.
    }, []);

    const value = React.useMemo(
        () => ({ siteStatus, setSiteStatus }),
        [siteStatus, setSiteStatus]
    );

    return (
        <SiteStatusContext.Provider value={value}>
            {children}
        </SiteStatusContext.Provider>
    );
}

export const useSiteStatus = () => useContext(SiteStatusContext);
